import { useEffect, useRef } from "react";
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { impactStats } from "@/mocks/sustainability";

export default function ImpactStats() {
  const root = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!root.current) return;
    const ctx = gsap.context(() => {
      gsap.from(".sus-stat-card", {
        y: 60,
        opacity: 0,
        duration: 1,
        stagger: 0.12,
        ease: "power3.out",
        scrollTrigger: { trigger: root.current, start: "top 70%" },
      });

      document.querySelectorAll<HTMLElement>(".sus-stat-num").forEach((el) => {
        const target = parseFloat(el.dataset.value || "0");
        const suffix = el.dataset.suffix || "";
        const decimals = parseInt(el.dataset.decimals || "0", 10);
        const obj = { val: 0 };
        gsap.to(obj, {
          val: target,
          duration: 2,
          ease: "power2.out",
          scrollTrigger: { trigger: el, start: "top 80%" },
          onUpdate: () => {
            el.textContent = obj.val.toFixed(decimals) + suffix;
          },
        });
      });
    }, root);
    return () => ctx.revert();
  }, []);

  return (
    <section ref={root} className="bg-bone py-24 md:py-36 px-6 md:px-10">
      <div className="max-w-6xl mx-auto">
        <div className="text-center max-w-2xl mx-auto mb-14 md:mb-20">
          <p className="text-[11px] font-mono uppercase tracking-[0.22em] text-ink/50 mb-4">
            ✦ By the numbers
          </p>
          <h2 className="font-display text-4xl md:text-6xl text-ink tracking-tightest font-light leading-[0.95]">
            Impact, <span className="font-display text-rust">measured honestly.</span>
          </h2>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 md:gap-8">
          {impactStats.map((stat) => (
            <div
              key={stat.label}
              className="sus-stat-card p-8 md:p-10 rounded-[24px] bg-sand/40 hover:bg-sand/70 transition-colors"
            >
              <p className="font-display text-5xl md:text-6xl text-ink tracking-tightest font-light">
                <span
                  className="sus-stat-num"
                  data-value={stat.value}
                  data-suffix={stat.suffix}
                  data-decimals={stat.decimals}
                >
                  0{stat.suffix}
                </span>
              </p>
              <p className="mt-3 text-xs uppercase tracking-[0.18em] text-ink/50 font-mono">
                {stat.label}
              </p>
              <p className="mt-4 text-ink/60 text-sm leading-relaxed">
                {stat.description}
              </p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}